import * as React from "react";
import { observer } from "mobx-react-lite";
import { QueryUI } from "../../components/query/queryUI";
import { ItemStoreQueryMobx, ItemStoreQueryModel } from "../../mst/query";
import { objectToArray } from "../../services/utils";
import { FormButtons } from "../../components/forms/formButtons";
import { notification } from "antd";
import { useDetailContext } from "../../components/detail/detailContext";
import { GroupMobx } from "../../mst/kinds/group";
import { trySaveWithReleaseWarning } from "../../utils/releaseWarning";

interface Props {
  group: GroupMobx;
}
const GroupUserQueryRaw: React.FC<Props> = ({ group }) => {
  const { fetchItem } = useDetailContext();

  const [isLoading, setIsLoading] = React.useState(false);

  function getDefaultUseQuery() {
    return !!group.memberQuery && group.memberQuery.spec.terms.length > 0;
  }
  const [useQuery, setUseQuery] = React.useState(getDefaultUseQuery());

  function getDefaultQueryModel() {
    if (!group.memberQuery) {
      return;
    }

    return {
      kind: "user",
      spec: JSON.parse(JSON.stringify(group.memberQuery.spec)),
    };
  }
  const [query, setQuery] = React.useState<ItemStoreQueryMobx>(
    ItemStoreQueryModel.create({ previousModel: getDefaultQueryModel() as any, model: getDefaultQueryModel() as any }),
  );

  function tryHandleSave() {
    trySaveWithReleaseWarning(group, handleSave);
  }

  async function handleSave() {
    try {
      setIsLoading(true);
      const body = {
        memberQuery: useQuery ? query.model.toQueryObj : null,
      };
      await group.patch(body);
      notification.success({ message: "Success", description: "Updated group user query" });
      setIsLoading(false);
      handleReset();
    } catch (e) {
      setIsLoading(false);
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({ message: "Failed", description: errorMessage });

      if (e.response.status === 409) {
        if (fetchItem) {
          await fetchItem();
          notification.info({
            message: "Updated Item",
            description: "Fetched the latest version of the item and discarded changes.",
          });
        }
      }
    }
  }

  async function handleReset() {
    setUseQuery(getDefaultUseQuery());
    setQuery(
      ItemStoreQueryModel.create({
        previousModel: getDefaultQueryModel() as any,
        model: getDefaultQueryModel() as any,
      }),
    );
  }

  let isDirty = false;
  if (useQuery !== getDefaultUseQuery()) isDirty = true;
  if (query.isDirty) isDirty = true;

  return (
    <div>
      <QueryUI
        idPrefix={`group-detail-member`}
        kind={"user"}
        query={query}
        useQuery={useQuery}
        setUseQuery={setUseQuery}
        evaluatedItemFn={(item: any) => ({
          id: item.id,
          name: item.name,
          description: "",
          tags: objectToArray(item.tags),
        })}
      />
      <FormButtons
        loading={isLoading}
        onReset={handleReset}
        onSave={tryHandleSave}
        resetDisabled={!isDirty || isLoading}
        saveDisabled={!isDirty || isLoading}
      />
    </div>
  );
};

export const GroupUserQuery = observer(GroupUserQueryRaw);
